/**
 * Meal action helpers — skip / unskip / remind. Mirrors iOS
 * `LocalDataStore.skipMeal(_:)` / `unskipMeal(_:)`
 * (`Model/LocalDataStore.swift:1141-1180`) and the
 * `sendMealVoteReminder` callable Cloud Function in
 * `functions/src/pushes.ts`.
 *
 * Skip / unskip are direct Firestore writes (no Cloud Function);
 * remind invokes the existing CF that iOS + Android already call.
 */

import {
  collection,
  deleteField,
  getDocs,
  query,
  updateDoc,
  where,
  writeBatch,
} from "firebase/firestore";
import { getApp } from "firebase/app";
import { getFunctions, httpsCallable } from "firebase/functions";
import { db } from "@/lib/firebase";

/**
 * Mark every `mealSuggestions` doc with the given `meal_type` for
 * the given household as `is_skipped: true`. Mirrors iOS
 * `skipMeal(mealType:)` write semantics.
 */
export async function skipMealType(
  householdId: string,
  mealType: string,
): Promise<void> {
  const ref = collection(db, `households/${householdId}/mealSuggestions`);
  const q = query(ref, where("meal_type", "==", mealType));
  const snap = await getDocs(q);
  if (snap.empty) return;
  const batch = writeBatch(db);
  for (const d of snap.docs) {
    batch.update(d.ref, { is_skipped: true });
  }
  await batch.commit();
}

/**
 * Restore meals — delete the `is_skipped` field so the docs revert
 * to "active" state. iOS uses `FieldValue.delete()` here; the web
 * SDK equivalent is `deleteField()`.
 */
export async function unskipMealType(
  householdId: string,
  mealType: string,
): Promise<void> {
  const ref = collection(db, `households/${householdId}/mealSuggestions`);
  const q = query(ref, where("meal_type", "==", mealType));
  const snap = await getDocs(q);
  if (snap.empty) return;
  for (const d of snap.docs) {
    await updateDoc(d.ref, { is_skipped: deleteField() });
  }
}

/**
 * Invoke the `sendMealVoteReminder` Cloud Function — same callable
 * iOS + Android use to nudge household members to vote on meals.
 * Returns the count of pushes sent so callers can show a toast.
 */
export async function sendMealVoteReminder(
  householdId: string,
  mealType: string,
): Promise<number> {
  const functions = getFunctions(getApp());
  const reminder = httpsCallable<
    { householdId: string; mealType: string },
    { sent?: number }
  >(functions, "sendMealVoteReminder");
  const result = await reminder({ householdId, mealType });
  return result.data?.sent ?? 0;
}
